import React from 'react';
import IndustryPageTemplate from '../../components/IndustryPageTemplate';
import SEO from '../../components/SEO';

const MassageTherapy = () => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How can Local Business Heroes help my massage therapy business?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Local Business Heroes helps massage therapy businesses capture more leads, automate follow-ups, reduce no-shows, and grow faster. Rated 4.9/5 by 1,200+ UK businesses."
        }
      },
      {
        "@type": "Question",
        "name": "How much does it cost for massage therapy businesses?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Plans start at £297/month with a 14-day trial for just £5. Most massage therapy businesses see ROI within the first month."
        }
      },
      {
        "@type": "Question",
        "name": "Do I need technical skills to use it?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "No. Local Business Heroes is designed for busy business owners with zero tech experience. Setup takes 5 minutes with UK-based support."
        }
      },
      {
        "@type": "Question",
        "name": "Will it work for my massage therapy business?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. Local Business Heroes is specifically designed for local service businesses like massage therapy. Features include appointment scheduling, automated reminders, lead capture, and customer management."
        }
      }
    ]
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "CRM for Massage Therapy - Local Business Heroes",
    "applicationCategory": "BusinessApplication",
    "offers": {
      "@type": "Offer",
      "price": "297",
      "priceCurrency": "GBP"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "1200"
    }
  };

  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Industries", url: "/industries" },
    { name: "Massage Therapy CRM", url: "/industries/massage-therapy" }
  ];

  const industryData = {
    name: 'Massage Therapy',
    icon: '💆',
    title: 'Fill Your Massage Schedule & Turn One-Time Clients Into Regulars',
    subtitle: 'Online booking, wellness membership automation, and self-care reminders that build loyal clientele',
    
    challenges: [
      'Clients treating massage as occasional luxury instead of regular wellness',
      'High no-show rates leaving empty appointment slots',
      'Difficulty getting clients to commit to package or membership plans',
      'Manual appointment scheduling consuming time between sessions',
      'One-time stress-relief clients not returning for regular maintenance',
      'Hard to educate clients about benefits of consistent massage therapy',
      'Competing with spa chains and massage franchise pricing',
      'Not enough reviews to stand out in competitive local market'
    ],
    
    solutions: [
      'Online booking allows clients to schedule sessions 24/7 without phone calls',
      'Automated wellness reminder campaigns promote regular massage for health benefits',
      'Membership package promotions encourage clients to commit to monthly sessions',
      'SMS reminders reduce no-shows from 18% to under 3%',
      'Educational email sequences explain benefits of consistent massage therapy',
      'Rebook-before-leaving automation ensures clients schedule next session',
      'Seasonal wellness campaigns target stress periods (holidays, tax season, etc.)',
      'Review automation after sessions builds online reputation and credibility'
    ],
    
    features: [
      '24/7 Online Booking with Service Type Selection',
      'Automated Appointment Reminders (48hr, 24hr, 2hr)',
      'Membership & Package Promotion Campaigns',
      'Wellness Education Email Series',
      'Monthly Massage Reminder Automation',
      'Rebook Prompt Before Client Leaves',
      'Seasonal Stress-Relief Campaign Automation',
      'Cancellation Waitlist Management',
      'Client Intake Form Automation',
      'Google Review Request After Session',
      'Birthday & Anniversary Special Offers',
      'Gift Certificate Management'
    ],
    
    results: [
      '70% reduction in no-shows',
      '65% of clients on monthly memberships',
      'Fill appointment book 90%+ capacity',
      'Increase client visit frequency by 85%',
      'Average 4.9★ Google rating',
      'Reduce admin time by 8 hours/week'
    ],
    
    reviews: [
      {
        author: 'Sarah Mitchell',
        location: 'Manchester',
        text: 'The membership promotion campaigns have transformed my business. 65% of my clients are now on monthly massage plans instead of sporadic visits. That\'s predictable income and I can finally plan my schedule and finances properly.',
        result: '65% clients on monthly memberships'
      },
      {
        author: 'Emma Taylor',
        location: 'Birmingham',
        text: 'The wellness education sequences have helped clients understand massage as healthcare, not just pampering. Client visit frequency has increased 85%. People are coming monthly instead of twice a year. Better for them and better for my business.',
        result: '85% increase in visit frequency'
      },
      {
        author: 'Jessica Roberts',
        location: 'Leeds',
        text: 'The no-show reduction has been life-changing. I used to lose £800-1,000 per month to no-shows. Now it\'s under £100. The automated reminders work perfectly and the cancellation waitlist fills slots within minutes.',
        result: '£9,600 annual revenue recovered'
      },
      {
        author: 'Amy Chen',
        location: 'Bristol',
        text: 'Online booking has made my solo practice feel professional. Clients can book at midnight or during work breaks. 75% of my appointments now come through online booking. I have my evenings back instead of answering booking calls.',
        result: '75% of bookings now online'
      },
      {
        author: 'Rachel Foster',
        location: 'Liverpool',
        text: 'The rebook-before-leaving automation ensures clients schedule their next massage before they leave. My retention went from 55% to 82%. That\'s consistent clients and full appointment books instead of constantly finding new people.',
        result: '82% client retention rate'
      },
      {
        author: 'Laura Bennett',
        location: 'Glasgow',
        text: 'The review automation got me from 12 reviews to 97 in 8 months. I\'m now the top-rated massage therapist in Glasgow. New clients mention the reviews and the professional booking system. Enquiries have tripled.',
        result: '97 reviews, enquiries tripled'
      }
    ]
  };

  return (
    <>
      <SEO 
        title="CRM for Massage Therapy UK | Marketing Automation | Local Business Heroes"
        description="Rated 4.9/5. CRM and marketing automation for massage therapy businesses. Capture more leads, automate follow-up, grow faster. Start £5 trial today."
        keywords="CRM for massage therapy UK, massage therapy business software, massage therapy customer management, massage therapy lead generation UK"
        url="/industries/massage-therapy"
        schemaMarkup={[faqSchema, localBusinessSchema]}
        breadcrumbs={breadcrumbs}
      />
      <IndustryPageTemplate industry={industryData} />
    </>
  );
};

export default MassageTherapy;